import { redirect } from 'react-router-dom';

import { bindPromiseAction } from 'app-components/flux/core/actionHelpers';
import { reportConfigurationsActions } from 'app-components/flux/report-configurations';
import type {
  OpenReportConfigurationPayload,
  ReportConfiguration,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getReportTrackingProps, tracker } from 'app-components/utils/core';
import type { AppDispatch } from 'store';

import { consumeReportNotificationPath } from 'navigation/reportNotification';

type OpenReportConfiguration = (payload: OpenReportConfigurationPayload) => Promise<ReportConfiguration | undefined>;

type ReportRouteLoaderArgs = {
  request: Request;
  reports: boolean;
  dispatch: AppDispatch;
  openReportConfiguration?: OpenReportConfiguration;
};

const openReportConfigurationViaStore = (dispatch: AppDispatch): OpenReportConfiguration =>
  bindPromiseAction<OpenReportConfigurationPayload, Record<string, never>, ReportConfiguration | undefined>(
    dispatch,
    reportConfigurationsActions.openReportConfiguration,
  );

export const loadReportRoute = async ({
  request,
  reports,
  dispatch,
  openReportConfiguration = openReportConfigurationViaStore(dispatch),
}: ReportRouteLoaderArgs): Promise<Response | null> => {
  const notificationOpen = consumeReportNotificationPath(request.url);
  if (!reports) return redirect('/oops');

  const requestUrl = new URL(request.url);
  const parentConfigId = requestUrl.searchParams.get('parentConfigId');
  if (!parentConfigId) return null;

  try {
    const opened = await openReportConfiguration({ parentConfigId, parentPersistentType: 'PREDEFINED' });
    if (opened?.id) {
      if (notificationOpen) {
        tracker.track(tracker.events.reportOpened, {
          ...getReportTrackingProps(opened),
          source: 'notification',
        });
      }
      return redirect(`/reports/${opened.id}`);
    }
  } catch {
    // Saga surfaces an error toast; fall through to Reports home.
  }

  return redirect('/reports');
};
